import { useEffect, useMemo, useRef, useState } from 'react';
import { GRADING_THRESHOLDS } from '../../../../lib/constants.js';
import { useCurrentUserProfile } from '../../../../lib/useCurrentUserProfile.js';
import {
  findLanguageUseRuleResultsForTask,
  resolveLanguageUseBoxSources,
  type LanguageUseBoxSource,
  type LanguageUseRuleResultLike,
  type LanguageUseSchemeLike,
} from '../../../../../shared/grammar-rules-resolution.js';
import { cumulativeTaskRefs } from '../../../../../db/utils/task-ref.js';
import { findTaskTreeNode } from '../../shared/task-tree.js';
import type { BoxMode } from './components/box-mode-toggle.js';
import { useGradingDom } from './hooks/use-grading-dom.js';
import type {
  CombinedCorrectionProps,
  GradingResult,
  LanguageUseConfidenceSource,
  SaveTarget,
  Submission,
  UnifiedBoundingBox,
} from './utils.js';
import {
  calculateMaxScore,
  type ConfidenceLevel,
  findBbxUncertainties,
  findLanguageUseUncertainties,
  findUncertainties,
  flattenSubTasksWithMeta,
  getConfidenceLevelSignal,
  getLanguageUseConfidenceLevelSignal,
  getSubTaskByFlatIndex,
  parseScore,
} from './utils.js';

const EMPTY_BOXES: UnifiedBoundingBox[] = [];

interface SelectableTaskEntry {
  mainTaskIdx: number;
  subTaskIdx: number | null;
  mainTaskNumber: string;
  fullTaskNumber: string;
  depth: number;
}

export function useGradingState(props: CombinedCorrectionProps) {
  const {
    submissions,
    exam,
    mode: modeProp = 'correction',
    initialSubmissionIdx,
    initialTaskKey,
    initialBoxMode,
    focusFirstLowConfidence,
    focusFirstLowConfidenceMode,
    onSaveUpdate,
    onOpenCanvas,
    refiningGradingSchemeTaskIds,
    refiningGradingSchemeLanguageUseTaskIds,
    regradingContentKeys,
    regradingLanguageUseKeys,
    queuedContentKeys,
    queuedLanguageUseKeys,
  } = props;

  const { role } = useCurrentUserProfile();

  // Mode is seeded from the prop but held locally so the in-canvas correction⇄grammar toggle can
  // switch it without a round-trip to the parent. Resync when the prop changes (e.g. the parent
  // opens the canvas in a different workflow step).
  const [mode, setMode] = useState(modeProp);
  useEffect(() => {
    setMode(modeProp);
  }, [modeProp]);

  const isAnswersMode = mode === 'answers';
  const isGrammarMode = mode === 'grammar';

  // --- 1. Isoliere UI-Events und Refs ---
  const dom = useGradingDom();
  dom.refs.isGrammarModeRef.current = isGrammarMode;

  // --- 2. Core State ---
  const hasInitializedFromProps = useRef(false);
  const didAutoFocusFirstLowRef = useRef(false);
  const lastSyncedContextKeyRef = useRef<string | null>(null);
  const pendingPageSyncContextKeyRef = useRef<string | null>(null);
  // Exact context key the initialBoxMode prop targets, so the context-switch reset effect below can
  // recognize "this context change is the props-driven initial navigation" (and preserve the seeded
  // box mode) vs. any other context change (which should reset to 'content' as usual).
  const pendingInitialBoxModeContextKeyRef = useRef<string | null>(null);

  const [currentSubmissionIdx, setCurrentSubmissionIdx] = useState(initialSubmissionIdx ?? 0);
  const [currentMainTaskIdx, setCurrentMainTaskIdx] = useState(0);
  const [currentSubTaskIdx, setCurrentSubTaskIdx] = useState<number | null>(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [localTaskBoxes, setLocalTaskBoxes] = useState<UnifiedBoundingBox[]>([]);

  const [isEditingText, setIsEditingText] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [isEditingGrading, setIsEditingGrading] = useState(false);
  const [editedGradingText, setEditedGradingText] = useState('');
  const [editedScore, setEditedScore] = useState<number>(0);
  const [localIsApproved, setLocalIsApproved] = useState<boolean>(false);
  const [localLanguageUseIsApproved, setLocalLanguageUseIsApproved] = useState<boolean>(false);
  const [isGradingSchemeCollapsed, setIsGradingSchemeCollapsed] = useState<boolean>(true);
  // Per-box content/language-use toggle (distinct from the whole-canvas `mode` above) — reset
  // whenever the current task/submission context changes, see the context-switch effect below.
  const [boxMode, setBoxMode] = useState<BoxMode>('content');
  const [activeLanguageUseSourceKey, setActiveLanguageUseSourceKey] = useState<string | null>(null);

  // --- 3. Computed Context ---
  const currentSubmission = submissions[currentSubmissionIdx];
  const pages = currentSubmission?.pages || [];
  const currentMainTask = currentSubmission?.taskSubmissions?.[currentMainTaskIdx];
  const currentSubTask = getSubTaskByFlatIndex(currentMainTask, currentSubTaskIdx);

  const matchingMainTaskByNumber = exam?.tasks?.find(
    task => String(task.taskNumber) === String(currentMainTask?.taskNumber),
  );
  const matchingMainTaskByIndex = currentMainTaskIdx >= 0 ? exam?.tasks?.[currentMainTaskIdx] : undefined;
  const currentMainExamTask = matchingMainTaskByNumber ?? matchingMainTaskByIndex;

  const unwrapGradingScheme = (
    raw: any,
  ): { criteria?: string; maxPoints?: number } | undefined => {
    if (!raw) return undefined;
    return typeof raw === 'object' && 'doc' in raw ? raw.doc : raw;
  };

  // qualifiedTaskNumber is the "|"-joined ancestor path (e.g. "2|a") — the same reference
  // LanguageUseScheme.dependsOn now stores (see build-exam-tasks.ts's collectAllTaskNumbers), since a
  // bare local taskNumber like "a" is ambiguous whenever it's reused as a subtask label under more
  // than one parent. Computed alongside the node lookup so the flat-index walk isn't duplicated.
  const { node: currentExamTaskForScheme, qualifiedTaskNumber: currentExamTaskQualifiedNumber } = useMemo((): {
    node: any;
    qualifiedTaskNumber: string;
  } => {
    const mainTaskNumber = String((currentMainExamTask as any)?.taskNumber ?? '');
    if (!currentMainExamTask) return { node: undefined, qualifiedTaskNumber: '' };
    if (currentSubTaskIdx === null) return { node: currentMainExamTask, qualifiedTaskNumber: mainTaskNumber };
    const flatRef = { value: 0 };
    const findByFlatIndex = (tasks: any[], parentPath: string[]): { node: any; path: string[] } | undefined => {
      for (const task of tasks ?? []) {
        const path = [...parentPath, String(task?.taskNumber ?? '')];
        if (flatRef.value === currentSubTaskIdx) return { node: task, path };
        flatRef.value++;
        const found = findByFlatIndex(Array.isArray(task.subTasks) ? task.subTasks : [], path);
        if (found) return found;
      }
      return undefined;
    };
    const result = findByFlatIndex(
      Array.isArray((currentMainExamTask as any).subTasks) ? (currentMainExamTask as any).subTasks : [],
      [mainTaskNumber],
    );
    return { node: result?.node, qualifiedTaskNumber: result ? result.path.join('|') : mainTaskNumber };
  }, [currentMainExamTask, currentSubTaskIdx]);

  const currentGradingScheme = unwrapGradingScheme(currentExamTaskForScheme?.gradingScheme);
  const gradingSchemeCriteria: string = currentGradingScheme?.criteria ?? '';
  const gradingSchemeMaxPoints: number = Number.isFinite(Number(currentGradingScheme?.maxPoints))
    ? Number(currentGradingScheme?.maxPoints)
    : 0;

  // --- 3b. Language-use box-mode sources: every rule is a LanguageUseScheme now (solo 1-member or
  // multi-member), a task can belong to more than one. Grading mechanics still differ by member count
  // (see shared/grammar-rules-resolution.ts module doc), but every RESULT — solo or group — lives in
  // the shared Submission.languageUseRuleResults, keyed by languageUseSchemeId. ---
  const languageUseSchemeRules: LanguageUseSchemeLike[] = ((exam as any)?.languageUseSchemeRules ??
    []) as LanguageUseSchemeLike[];
  const languageUseBoxSources: LanguageUseBoxSource[] = useMemo(
    () => resolveLanguageUseBoxSources(currentExamTaskQualifiedNumber, languageUseSchemeRules),
    [currentExamTaskQualifiedNumber, languageUseSchemeRules],
  );
  const activeLanguageUseSource: LanguageUseBoxSource | null =
    languageUseBoxSources.find(source => source.key === activeLanguageUseSourceKey) ?? languageUseBoxSources[0] ?? null;

  const currentGroupGradingScheme: LanguageUseSchemeLike | undefined = activeLanguageUseSource
    ? languageUseSchemeRules.find(scheme => String(scheme._id) === activeLanguageUseSource.key)
    : undefined;

  // Root task id — genuinely root-scoped (unlike the two flags below): a `grading` job dispatches per
  // main task, never per subtask, so this is exactly what `regradingContentKeys` below is keyed by.
  const gsRootTaskId = currentMainExamTask?._id ? String(currentMainExamTask._id) : undefined;

  // Read-only echo of the ms-gs canvas's GS box spinner (see SolutionGradingCheckProps in ms-gs/utils.ts)
  // — same job-sourced Sets, resolved against this view's exam/task shape instead of ms-gs's flat Task[].
  // The sets already hold the exact gradable-node ids a live job targets, so this checks the current
  // (sub)task's OWN id, not its root's — a call covers a whole main-task subtree, but rarely targets
  // every node in it.
  const isGradingSchemeContentRefining =
    !!currentExamTaskForScheme?._id && !!refiningGradingSchemeTaskIds?.has(String(currentExamTaskForScheme._id));
  // `dependsOn` entries are pipe-joined ancestor paths (e.g. "2|a") — walked level by level against
  // exam.tasks (see task-tree.ts) to find each dependent task's own node (and so its own id), since a
  // multi-member group can depend on a subtask several levels deep, not just a top-level main task.
  const isGradingSchemeLanguageUseRefining = !!currentGroupGradingScheme?.dependsOn?.some(dep => {
    const depTaskId = findTaskTreeNode((exam as any)?.tasks ?? [], String(dep), '|')?._id;
    return !!depTaskId && !!refiningGradingSchemeLanguageUseTaskIds?.has(String(depTaskId));
  });

  // Genuinely per (submission, task/scheme) — see streamLiveGradingJobsByExamIdSubscription. taskKey
  // matches the SAME exam-task root id gsRootTaskId already resolves (grading.job.ts's payload.taskKey is
  // getTaskKey(root) too), so it's reused here rather than re-derived.
  const currentSubmissionId = currentSubmission?._id ? String(currentSubmission._id) : undefined;

  /**
   * Whether a live grading row covers THIS node — its whole main task, or one of the refs it named.
   *
   * Shares its walk with the server's `qualifiedPathMatchesRequestedRefs` via `cumulativeTaskRefs`, which is
   * what keeps the box that shows a spinner exactly the node that gets regraded: a row scoped to `1.1a.i`
   * lights that box and nothing beside it, while a row scoped to the parent `1.1a` lights its children too,
   * because regrading a node regrades its subtree.
   *
   * Keys come from chat.tsx's `contentKeysOf`, built from the same normalisation — a ref is stored with every
   * separator stripped, so "1.1a.i" and the path segments 1 | 1a | i both reduce to `11ai`.
   */
  const matchesRegradeKeys = (keys: Set<string> | undefined): boolean => {
    if (!keys || !currentSubmissionId) return false;
    if (gsRootTaskId && keys.has(`${currentSubmissionId}::${gsRootTaskId}`)) return true;
    return cumulativeTaskRefs(currentExamTaskQualifiedNumber).some(ref =>
      keys.has(`${currentSubmissionId}::ref:${ref}`),
    );
  };

  const isRegradingContent = matchesRegradeKeys(regradingContentKeys);
  const isRegradingLanguageUse =
    !!currentSubmissionId &&
    !!activeLanguageUseSource?.key &&
    !!regradingLanguageUseKeys?.has(`${currentSubmissionId}::${activeLanguageUseSource.key}`);
  // Queued rather than running — wording only, so it is derived from the same keys and never widens which
  // boxes show a spinner.
  const isRegradingContentQueued = isRegradingContent && matchesRegradeKeys(queuedContentKeys);
  const isRegradingLanguageUseQueued =
    isRegradingLanguageUse && !!queuedLanguageUseKeys?.has(`${currentSubmissionId}::${activeLanguageUseSource?.key}`);

  const currentLanguageUseRuleResult: LanguageUseRuleResultLike | undefined = activeLanguageUseSource
    ? (currentSubmission?.languageUseRuleResults ?? []).find(
        result => String(result.languageUseSchemeId) === activeLanguageUseSource.key,
      )
    : undefined;

  // Rule text/points always come from the LanguageUseScheme now, regardless of member count.
  const currentLanguageUseMaxScore: number = Math.max(0, Number(currentGroupGradingScheme?.maxPoints) || 0);
  const currentLanguageUseCriteria: string = currentGroupGradingScheme?.criteria ?? '';

  const currentSaveTarget: SaveTarget =
    boxMode !== 'languageUse'
      ? { kind: 'content' }
      : { kind: 'group', languageUseSchemeId: activeLanguageUseSource?.key ?? '' };

  const findExamTaskByPath = (path: string[]): any | undefined => {
    if (!exam?.tasks || path.length === 0) return undefined;
    let currentList: any[] = exam.tasks as any[];
    let currentNode: any | undefined;

    for (const segment of path) {
      const normalizedSegment = String(segment ?? '').trim();
      if (!normalizedSegment) return undefined;
      currentNode = currentList.find(task => String(task?.taskNumber ?? '').trim() === normalizedSegment);
      if (!currentNode) return undefined;
      currentList = Array.isArray(currentNode.subTasks) ? currentNode.subTasks : [];
    }

    return currentNode;
  };

  const isNeedsAnswerPath = (path: string[]): boolean => {
    const node = findExamTaskByPath(path);
    if (!node) return true;
    return node.needsAnswer !== false;
  };

  const selectableTaskEntries = useMemo<SelectableTaskEntry[]>(() => {
    if (!currentSubmission?.taskSubmissions?.length) return [];
    const out: SelectableTaskEntry[] = [];

    currentSubmission.taskSubmissions.forEach((mainTask, mainIdx) => {
      const mainTaskNumber = String((mainTask as any)?.taskNumber ?? (mainTask as any)?.tasknumber ?? '?').trim();
      if (!mainTaskNumber) return;

      if (isNeedsAnswerPath([mainTaskNumber])) {
        out.push({
          mainTaskIdx: mainIdx,
          subTaskIdx: null,
          mainTaskNumber,
          fullTaskNumber: mainTaskNumber,
          depth: 0,
        });
      }

      const flatSubTasks = flattenSubTasksWithMeta(mainTask);
      flatSubTasks.forEach(entry => {
        const path = [mainTaskNumber, ...entry.numberPath];
        if (!isNeedsAnswerPath(path)) return;
        out.push({
          mainTaskIdx: mainIdx,
          subTaskIdx: entry.flatIndex,
          mainTaskNumber,
          fullTaskNumber: `${mainTaskNumber}.${entry.numberPath.join('.')}`,
          depth: entry.depth,
        });
      });
    });

    return out;
  }, [currentSubmission, exam]);

  const currentSelectableTaskIndex = useMemo(
    () =>
      selectableTaskEntries.findIndex(
        entry => entry.mainTaskIdx === currentMainTaskIdx && entry.subTaskIdx === currentSubTaskIdx,
      ),
    [selectableTaskEntries, currentMainTaskIdx, currentSubTaskIdx],
  );

  const showExcerptOnlyForMainTask = currentSubTaskIdx === null && currentMainExamTask?.needsAnswer === false;
  const currentTaskBoxes = useMemo(() => {
    const boxes = currentSubTaskIdx !== null ? currentSubTask?.boundingBoxes : currentMainTask?.boundingBoxes;
    return Array.isArray(boxes) ? boxes : EMPTY_BOXES;
  }, [currentSubTaskIdx, currentSubTask, currentMainTask]);

  const currentGradingResultRaw =
    currentSubTaskIdx !== null ? currentSubTask?.gradingResult : currentMainTask?.gradingResult;
  const currentGradingResult = ((currentGradingResultRaw as any)?.doc || currentGradingResultRaw) as
    | GradingResult
    | undefined;

  const currentGrammarEvaluationRaw =
    currentSubTaskIdx !== null ? currentSubTask?.grammarEvaluation : currentMainTask?.grammarEvaluation;
  const currentGrammarEvaluation = ((currentGrammarEvaluationRaw as any)?.doc || currentGrammarEvaluationRaw) as
    | import('./utils.js').GrammarEvaluationView
    | undefined;

  // Folds every LanguageUseScheme result this task is linked to into one signal — shared by the
  // box-mode toggle's secondary dot (current submission) and getSubmissionConfidenceLevel (any
  // submission).
  const getLanguageUseConfidenceLevelForSubmission = (
    languageUseRuleResults: LanguageUseRuleResultLike[] | undefined,
  ): ConfidenceLevel => {
    const sources: LanguageUseConfidenceSource[] = findLanguageUseRuleResultsForTask(
      currentExamTaskQualifiedNumber,
      languageUseSchemeRules,
      languageUseRuleResults,
    ).map(result => ({
      confidence: result.confidence,
      isApproved: result.isApproved,
      schemeId: result.languageUseSchemeId,
    }));
    return getLanguageUseConfidenceLevelSignal(sources);
  };

  // Dual confidence signal for the box-mode toggle's secondary dot: independent of which source is
  // displayed and independent of the (unchanged) content confidence driving the panels' primary
  // border color.
  const languageUseConfidenceDotLevel = useMemo(
    () => getLanguageUseConfidenceLevelForSubmission(currentSubmission?.languageUseRuleResults),
    [currentGradingScheme, currentExamTaskForScheme, languageUseSchemeRules, currentSubmission],
  );

  // Language-use counterparts of explanation/isApproved/score, sourced from the active source's
  // LanguageUseRuleResult — solo or multi-member alike.
  const currentLanguageUseExplanation: string = currentLanguageUseRuleResult?.explanation ?? '';
  const currentLanguageUseIsApproved: boolean = currentLanguageUseRuleResult?.isApproved === true;
  const currentLanguageUseConfidenceNum: number | null =
    currentLanguageUseRuleResult?.confidence != null ? Number(currentLanguageUseRuleResult.confidence) : null;
  const getLanguageUseDisplayedScore = (): number => {
    const raw = currentLanguageUseRuleResult?.actualPointsGiven ?? currentLanguageUseRuleResult?.score;
    const base = parseScore(raw) ?? 0;
    return Math.max(0, Math.min(base, currentLanguageUseMaxScore));
  };
  const languageUseDisplayedScore = getLanguageUseDisplayedScore();

  const currentContextKey = [
    currentSubmission?._id ?? `sub-${currentSubmissionIdx}`,
    currentMainTask?._id ?? `main-${currentMainTaskIdx}`,
    currentSubTask?._id ?? (currentSubTaskIdx === null ? 'sub-main' : `sub-${currentSubTaskIdx}`),
  ].join('::');

  const selectedBox = localTaskBoxes.find(b => b.id === selectedBoxId);
  const rawConfidence = currentGradingResult?.confidence;
  const confidenceNum = rawConfidence != null ? Number(rawConfidence) : null;
  const isGradingMediumConfidence =
    !isAnswersMode &&
    confidenceNum !== null &&
    confidenceNum < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE &&
    confidenceNum >= GRADING_THRESHOLDS.LOW_CONFIDENCE &&
    !localIsApproved;
  const isGradingLowConfidence =
    !isAnswersMode && confidenceNum !== null && confidenceNum < GRADING_THRESHOLDS.LOW_CONFIDENCE && !localIsApproved;
  const selectedBoxIsApproved = selectedBox?.approved === true;

  // Content dot for the box-mode toggle's "Inhalt" pill, mirroring the existing language-use dot —
  // same low/medium/none vocabulary, sourced from the content grading confidence above.
  const contentConfidenceDotLevel: ConfidenceLevel = isGradingLowConfidence
    ? 'low'
    : isGradingMediumConfidence
      ? 'medium'
      : 'none';

  // KI-Bewertung box coloring: the WORSE of content and language-use confidence, regardless of which
  // box mode is currently selected — a single-mode signal was too easy to miss when the other mode
  // was the one with low confidence. Stays red/amber until both are resolved.
  const aiGradingConfidenceLevel: ConfidenceLevel =
    isGradingLowConfidence || languageUseConfidenceDotLevel === 'low'
      ? 'low'
      : isGradingMediumConfidence || languageUseConfidenceDotLevel === 'medium'
        ? 'medium'
        : 'none';

  // --- 4. Ausgelagerte schwere Berechnungen ---
  const uncertainties = useMemo(() => {
    const raw =
      isAnswersMode || focusFirstLowConfidenceMode === 'bbx'
        ? findBbxUncertainties(submissions)
        : findUncertainties(submissions);

    return raw.filter(({ submissionIdx, mainTaskIdx, subTaskIdx }) => {
      const submission = submissions[submissionIdx];
      const mainTask = submission?.taskSubmissions?.[mainTaskIdx];
      if (!mainTask) return false;
      const mainTaskNumber = String((mainTask as any)?.taskNumber ?? (mainTask as any)?.tasknumber ?? '').trim();
      if (!mainTaskNumber) return false;
      if (subTaskIdx === null) return isNeedsAnswerPath([mainTaskNumber]);
      const entry = flattenSubTasksWithMeta(mainTask).find(e => e.flatIndex === subTaskIdx);
      if (!entry) return false;
      return isNeedsAnswerPath([mainTaskNumber, ...entry.numberPath]);
    });
  }, [focusFirstLowConfidenceMode, isAnswersMode, submissions, exam]);

  // Language-use counterpart to `uncertainties`, navigable independently (own nav button in TopBar).
  // Empty in answers mode: nothing has been graded yet there, content or language-use.
  const languageUseUncertainties = useMemo(() => {
    if (isAnswersMode) return [];
    const raw = findLanguageUseUncertainties(submissions, languageUseSchemeRules);
    return raw.filter(({ submissionIdx, mainTaskIdx, subTaskIdx }) => {
      const submission = submissions[submissionIdx];
      const mainTask = submission?.taskSubmissions?.[mainTaskIdx];
      if (!mainTask) return false;
      const mainTaskNumber = String((mainTask as any)?.taskNumber ?? (mainTask as any)?.tasknumber ?? '').trim();
      if (!mainTaskNumber) return false;
      if (subTaskIdx === null) return isNeedsAnswerPath([mainTaskNumber]);
      const entry = flattenSubTasksWithMeta(mainTask).find(e => e.flatIndex === subTaskIdx);
      if (!entry) return false;
      return isNeedsAnswerPath([mainTaskNumber, ...entry.numberPath]);
    });
  }, [isAnswersMode, submissions, languageUseSchemeRules, exam]);

  // Unified content+language-use uncertainty navigation (single "next uncertainty" button) — a task
  // can carry either or both signals at once, so entries are deduped by task/submission and tagged
  // with which side(s) triggered them.
  const combinedUncertainties = useMemo(() => {
    const keyOf = (u: { submissionIdx: number; mainTaskIdx: number; subTaskIdx: number | null }) =>
      `${u.submissionIdx}::${u.mainTaskIdx}::${u.subTaskIdx}`;
    const map = new Map<
      string,
      {
        submissionIdx: number;
        mainTaskIdx: number;
        subTaskIdx: number | null;
        hasContent: boolean;
        hasLanguageUse: boolean;
      }
    >();
    for (const u of uncertainties) {
      map.set(keyOf(u), { ...u, hasContent: true, hasLanguageUse: false });
    }
    for (const u of languageUseUncertainties) {
      const key = keyOf(u);
      const existing = map.get(key);
      if (existing) existing.hasLanguageUse = true;
      else map.set(key, { ...u, hasContent: false, hasLanguageUse: true });
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.mainTaskIdx !== b.mainTaskIdx) return a.mainTaskIdx - b.mainTaskIdx;
      const aSub = a.subTaskIdx === null ? -1 : a.subTaskIdx;
      const bSub = b.subTaskIdx === null ? -1 : b.subTaskIdx;
      if (aSub !== bSub) return aSub - bSub;
      return a.submissionIdx - b.submissionIdx;
    });
  }, [uncertainties, languageUseUncertainties]);

  const currentMaxScore = calculateMaxScore(
    exam,
    currentMainTask,
    currentMainExamTask,
    currentSubTask,
    currentSubTaskIdx,
  );

  const getDisplayedScore = (): number => {
    // Awarded points from the grading result (actualPointsGiven → score → 0); no parsing points out of the
    // explanation text. Clamped to the selected node's available points.
    const actualPointsGiven = parseScore((currentGradingResult as any)?.actualPointsGiven);
    const score = parseScore((currentGradingResult as any)?.score);
    const base = actualPointsGiven ?? score ?? 0;
    return Math.max(0, Math.min(base, currentMaxScore));
  };
  const displayedScore = getDisplayedScore();

  const getSubmissionConfidenceLevel = (submission: Submission | undefined): ConfidenceLevel => {
    if (!submission) return 'none';
    const submissionMainTask = submission.taskSubmissions?.[currentMainTaskIdx];
    const gradingResultRaw =
      currentSubTaskIdx !== null
        ? getSubTaskByFlatIndex(submissionMainTask, currentSubTaskIdx)?.gradingResult
        : submissionMainTask?.gradingResult;
    const gradingResult = ((gradingResultRaw as any)?.doc || gradingResultRaw) as GradingResult | undefined;
    const boxes =
      currentSubTaskIdx !== null
        ? getSubTaskByFlatIndex(submissionMainTask, currentSubTaskIdx)?.boundingBoxes
        : submissionMainTask?.boundingBoxes;
    const contentLevel = isAnswersMode
      ? getConfidenceLevelSignal(undefined, boxes)
      : getConfidenceLevelSignal(gradingResult, boxes);

    // Worst of content and language-use confidence, mirroring aiGradingConfidenceLevel — otherwise a
    // submission with a bad language-use grade but a fine content grade never turns the dot red/amber.
    // Skipped in answers mode, same as contentLevel above: nothing has been graded yet there.
    const languageUseLevel = isAnswersMode
      ? 'none'
      : getLanguageUseConfidenceLevelForSubmission(submission.languageUseRuleResults);

    return contentLevel === 'low' || languageUseLevel === 'low'
      ? 'low'
      : contentLevel === 'medium' || languageUseLevel === 'medium'
        ? 'medium'
        : 'none';
  };

  const getTaskConfidenceLevel = (
    submission: Submission | undefined,
    mainTaskIdx: number,
    subTaskIdx: number | null,
  ) => {
    if (!submission) return 'none' as const;
    const mainTask = submission.taskSubmissions?.[mainTaskIdx];
    if (!mainTask) return 'none' as const;

    if (subTaskIdx === null) {
      const gradingResult = ((mainTask.gradingResult as any)?.doc || mainTask.gradingResult) as
        | GradingResult
        | undefined;
      return isAnswersMode
        ? getConfidenceLevelSignal(undefined, mainTask.boundingBoxes)
        : getConfidenceLevelSignal(gradingResult, mainTask.boundingBoxes);
    }

    const subTask = getSubTaskByFlatIndex(mainTask, subTaskIdx);
    if (!subTask) return 'none' as const;
    const gradingResult = ((subTask.gradingResult as any)?.doc || subTask.gradingResult) as GradingResult | undefined;
    return isAnswersMode
      ? getConfidenceLevelSignal(undefined, subTask.boundingBoxes)
      : getConfidenceLevelSignal(gradingResult, subTask.boundingBoxes);
  };

  useEffect(() => {
    if (!selectableTaskEntries.length) return;
    const isCurrentSelectionSelectable = selectableTaskEntries.some(
      entry => entry.mainTaskIdx === currentMainTaskIdx && entry.subTaskIdx === currentSubTaskIdx,
    );
    if (isCurrentSelectionSelectable) return;

    const firstSelectable = selectableTaskEntries[0];
    if (!firstSelectable) return;
    setCurrentMainTaskIdx(firstSelectable.mainTaskIdx);
    setCurrentSubTaskIdx(firstSelectable.subTaskIdx);
  }, [selectableTaskEntries, currentMainTaskIdx, currentSubTaskIdx]);

  // --- 5. Logic Effects ---
  // Reset the per-box content/language-use toggle whenever the task/submission context changes — a
  // language-use source picked for one task has no meaning once the teacher navigates elsewhere.
  // Skips the one context change caused by initialBoxMode's props-driven navigation (see
  // pendingInitialBoxModeContextKeyRef in the hasInitializedFromProps effect below).
  useEffect(() => {
    if (pendingInitialBoxModeContextKeyRef.current === currentContextKey) {
      pendingInitialBoxModeContextKeyRef.current = null;
      return;
    }
    setBoxMode('content');
    setActiveLanguageUseSourceKey(null);
  }, [currentContextKey]);

  // Keep the active language-use source valid: default to the first source when entering
  // language-use mode or when the current task's sources change (e.g. after navigating tasks).
  useEffect(() => {
    if (boxMode !== 'languageUse' || languageUseBoxSources.length === 0) return;
    if (languageUseBoxSources.some(source => source.key === activeLanguageUseSourceKey)) return;
    setActiveLanguageUseSourceKey(languageUseBoxSources[0]?.key ?? null);
  }, [boxMode, languageUseBoxSources, activeLanguageUseSourceKey]);

  // A task's last language-use source can disappear without any navigation (e.g. its only rule was
  // deleted) — without this the panel is stuck in an empty language-use box with no way back to
  // "Inhalt".
  useEffect(() => {
    if (boxMode === 'languageUse' && languageUseBoxSources.length === 0) setBoxMode('content');
  }, [boxMode, languageUseBoxSources]);

  useEffect(() => {
    const isContextSwitch = lastSyncedContextKeyRef.current !== currentContextKey;
    if (isContextSwitch) {
      setLocalTaskBoxes(currentTaskBoxes.map(box => ({ ...box })));
      lastSyncedContextKeyRef.current = currentContextKey;
      pendingPageSyncContextKeyRef.current = currentContextKey;
      return;
    }
    setLocalTaskBoxes(prev => {
      if (prev.length === 0) return currentTaskBoxes.map(box => ({ ...box }));
      const merged = prev.map((localBox, index) => {
        const serverBox = currentTaskBoxes[index];
        if (!serverBox) return localBox;
        return {
          ...localBox,
          ocrExtractedText:
            typeof serverBox.ocrExtractedText === 'string' && serverBox.ocrExtractedText.length > 0
              ? serverBox.ocrExtractedText
              : localBox.ocrExtractedText,
          contentType: serverBox.contentType ?? localBox.contentType,
          confidence: serverBox.confidence ?? localBox.confidence,
          approved: serverBox.approved ?? localBox.approved,
          pageNumber: serverBox.pageNumber,
          // Trusted the same way pageNumber already is: without this, a box's position only ever
          // updates from a server push on the ONE context-switch snapshot (task/submission change) —
          // the drag/retry save that actually moves it lands later, while this same task is still
          // selected, and its new coordinates were silently dropped here forever. The result once
          // ocrRerunning clears (see below) is the position landing correctly; while it's still true,
          // this can visibly revert a just-dragged box to its still-old server position until the final
          // write lands — same as ocrExtractedText/confidence already do during that window, just now
          // true for position too, rather than position being the one field frozen on stale local state.
          x: serverBox.x,
          y: serverBox.y,
          width: serverBox.width,
          height: serverBox.height,
          // Authoritative, no fallback to the local value: the server always states this explicitly
          // (true while its OCR call is in flight, false once landed or failed), so the live value is
          // always the right one to show, never something to preserve across an update.
          ocrRerunning: serverBox.ocrRerunning === true,
        };
      });
      if (currentTaskBoxes.length > merged.length) {
        for (let i = merged.length; i < currentTaskBoxes.length; i += 1) {
          if (currentTaskBoxes[i]) merged.push({ ...currentTaskBoxes[i] } as UnifiedBoundingBox);
        }
      }
      return merged;
    });
  }, [currentContextKey, currentTaskBoxes]);

  useEffect(() => {
    if (pendingPageSyncContextKeyRef.current !== currentContextKey) return;

    const isTaskApproved = !isAnswersMode && currentGradingResult?.isApproved === true;
    const firstConfidenceBox = isTaskApproved
      ? undefined
      : currentTaskBoxes.find(box => {
          if (box?.approved === true) return false;
          const confidence = Number(box?.confidence);
          return Number.isFinite(confidence) && confidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE;
        });

    if (firstConfidenceBox) {
      setSelectedBoxId(firstConfidenceBox.id);
      if (firstConfidenceBox.pageNumber !== currentPage) setCurrentPage(firstConfidenceBox.pageNumber);
      pendingPageSyncContextKeyRef.current = null;
      return;
    }
    const firstTaskBox = currentTaskBoxes[0];
    if (isAnswersMode && firstTaskBox) {
      setSelectedBoxId(firstTaskBox.id);
      if (firstTaskBox.pageNumber !== currentPage) setCurrentPage(firstTaskBox.pageNumber);
      pendingPageSyncContextKeyRef.current = null;
    } else {
      setSelectedBoxId(null);
      if (firstTaskBox) {
        if (firstTaskBox.pageNumber !== currentPage) setCurrentPage(firstTaskBox.pageNumber);
        pendingPageSyncContextKeyRef.current = null;
      }
    }
  }, [
    currentMainTaskIdx,
    currentSubTaskIdx,
    currentSubmissionIdx,
    currentTaskBoxes,
    currentGradingResult,
    isAnswersMode,
  ]);

  useEffect(() => {
    setIsEditingText(false);
    setIsEditingGrading(false);
    setEditedText(selectedBox?.ocrExtractedText || '');
    setLocalIsApproved(currentGradingResult?.isApproved === true);
    setLocalLanguageUseIsApproved(currentLanguageUseIsApproved);
    if (boxMode === 'languageUse') {
      setEditedGradingText(currentLanguageUseExplanation);
      setEditedScore(languageUseDisplayedScore);
    } else {
      setEditedGradingText(currentGradingResult?.explanation || '');
      setEditedScore(displayedScore);
    }
  }, [
    selectedBoxId,
    selectedBox?.ocrExtractedText,
    currentGradingResult,
    currentSubmissionIdx,
    currentMainTaskIdx,
    currentSubTaskIdx,
    boxMode,
    activeLanguageUseSourceKey,
    currentLanguageUseExplanation,
    currentLanguageUseIsApproved,
    languageUseDisplayedScore,
  ]);

  useEffect(() => {
    if (!isAnswersMode) return;
    setSelectedBoxId(prev => {
      if (prev !== null) return prev;
      return currentTaskBoxes[0]?.id ?? null;
    });
  }, [isAnswersMode, currentTaskBoxes]);

  useEffect(() => {
    const boxesOnPage = localTaskBoxes.filter(b => b.pageNumber === currentPage);
    const firstBoxOnPage = boxesOnPage[0];
    if (!firstBoxOnPage) return;

    const currentSelectedBox = localTaskBoxes.find(b => b.id === selectedBoxId);
    if (currentSelectedBox?.pageNumber === currentPage) return;

    if (isAnswersMode) {
      setSelectedBoxId(firstBoxOnPage.id);
    } else if (selectedBoxId !== null) {
      const lowConfBox = boxesOnPage.find(
        b =>
          b.approved !== true &&
          Number.isFinite(Number(b.confidence)) &&
          Number(b.confidence) < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE,
      );
      setSelectedBoxId((lowConfBox ?? firstBoxOnPage).id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (!focusFirstLowConfidence || didAutoFocusFirstLowRef.current || !exam || !submissions.length) return;
    if (initialSubmissionIdx !== undefined || initialTaskKey !== undefined) return;

    if (uncertainties.length > 0) {
      const firstIssue = uncertainties[0];
      if (!firstIssue) return;
      setCurrentMainTaskIdx(firstIssue.mainTaskIdx);
      setCurrentSubTaskIdx(firstIssue.subTaskIdx);
      setCurrentSubmissionIdx(firstIssue.submissionIdx);
      didAutoFocusFirstLowRef.current = true;
    }
  }, [focusFirstLowConfidence, exam, submissions, initialSubmissionIdx, initialTaskKey, uncertainties]);

  useEffect(() => {
    if (hasInitializedFromProps.current || !submissions.length) return;
    if (initialSubmissionIdx !== undefined && initialSubmissionIdx >= 0 && initialSubmissionIdx < submissions.length) {
      setCurrentSubmissionIdx(initialSubmissionIdx);
    }
    if (initialTaskKey !== undefined) {
      const submission = submissions[initialSubmissionIdx ?? 0] || submissions[0];
      if (submission?.taskSubmissions) {
        const normalizedTaskKey = String(initialTaskKey).trim();
        const taskPath = normalizedTaskKey
          .split('.')
          .map(part => part.trim())
          .filter(Boolean);
        const mainTaskNumber = taskPath[0] ?? normalizedTaskKey;
        const foundMainTaskIdx = submission.taskSubmissions.findIndex(t => String(t.taskNumber) === mainTaskNumber);
        if (foundMainTaskIdx !== -1) {
          setCurrentMainTaskIdx(foundMainTaskIdx);
          const mainTask = submission.taskSubmissions[foundMainTaskIdx];

          const flatSubTasks = flattenSubTasksWithMeta(mainTask);
          let resolvedSubTaskIdx: number | null;
          if (taskPath.length > 1) {
            const nestedTaskKey = taskPath.slice(1).join('.');
            const exactNestedTask = flatSubTasks.find(entry => entry.numberPath.join('.') === nestedTaskKey);
            resolvedSubTaskIdx = exactNestedTask ? exactNestedTask.flatIndex : null;
          } else if (flatSubTasks.length > 0) {
            const subTaskLevels = flatSubTasks.map(entry => {
              const gradingResultRaw = (entry.task as any)?.gradingResult;
              const gradingResult = (gradingResultRaw?.doc || gradingResultRaw) as GradingResult | undefined;
              const level = isAnswersMode
                ? getConfidenceLevelSignal(undefined, (entry.task as any)?.boundingBoxes)
                : getConfidenceLevelSignal(gradingResult, (entry.task as any)?.boundingBoxes);
              return { flatIndex: entry.flatIndex, level };
            });

            const firstLowOrMedium = subTaskLevels.find(item => item.level === 'low' || item.level === 'medium');

            resolvedSubTaskIdx = firstLowOrMedium ? firstLowOrMedium.flatIndex : 0;
          } else {
            resolvedSubTaskIdx = null;
          }
          setCurrentSubTaskIdx(resolvedSubTaskIdx);

          if (initialBoxMode) {
            // Predict the exact context key this navigation resolves to (same formula as
            // currentContextKey below) so the reset effect above can recognize and preserve it.
            const targetSubTask = getSubTaskByFlatIndex(mainTask, resolvedSubTaskIdx);
            pendingInitialBoxModeContextKeyRef.current = [
              submission._id ?? `sub-${currentSubmissionIdx}`,
              mainTask?._id ?? `main-${foundMainTaskIdx}`,
              targetSubTask?._id ?? (resolvedSubTaskIdx === null ? 'sub-main' : `sub-${resolvedSubTaskIdx}`),
            ].join('::');
            setBoxMode(initialBoxMode);
          }
        }
      }
    }
    hasInitializedFromProps.current = true;
  }, [initialSubmissionIdx, initialTaskKey, initialBoxMode, submissions]);

  // --- 6. Actions ---
  // Unified content+language-use uncertainty navigation. Switches boxMode to match whichever signal
  // the target entry actually has (language-use-only entries land on the language-use box, since a
  // content view would show nothing relevant there).
  const handleNextUncertainty = () => {
    if (combinedUncertainties.length === 0) return;
    const currentIssueIdx = combinedUncertainties.findIndex(
      u =>
        u.submissionIdx === currentSubmissionIdx &&
        u.mainTaskIdx === currentMainTaskIdx &&
        u.subTaskIdx === currentSubTaskIdx,
    );
    const nextIssue =
      combinedUncertainties[currentIssueIdx === -1 ? 0 : (currentIssueIdx + 1) % combinedUncertainties.length];
    if (!nextIssue) return;

    // Predict the context key this navigation resolves to (same formula as currentContextKey above)
    // so the reset effect recognizes it and skips forcing boxMode back to 'content' right after —
    // same mechanism the initialBoxMode prop navigation uses.
    const nextSubmission = submissions[nextIssue.submissionIdx];
    const nextMainTask = nextSubmission?.taskSubmissions?.[nextIssue.mainTaskIdx];
    const nextSubTask = getSubTaskByFlatIndex(nextMainTask, nextIssue.subTaskIdx);
    pendingInitialBoxModeContextKeyRef.current = [
      nextSubmission?._id ?? `sub-${nextIssue.submissionIdx}`,
      nextMainTask?._id ?? `main-${nextIssue.mainTaskIdx}`,
      nextSubTask?._id ?? (nextIssue.subTaskIdx === null ? 'sub-main' : `sub-${nextIssue.subTaskIdx}`),
    ].join('::');

    setCurrentMainTaskIdx(nextIssue.mainTaskIdx);
    setCurrentSubTaskIdx(nextIssue.subTaskIdx);
    setCurrentSubmissionIdx(nextIssue.submissionIdx);
    setBoxMode(nextIssue.hasContent ? 'content' : 'languageUse');
  };

  const getTaskName = () => {
    if (!selectableTaskEntries.length) return 'Aufgabe waehlen...';
    const selectedEntry =
      (currentSelectableTaskIndex >= 0 ? selectableTaskEntries[currentSelectableTaskIndex] : null) ||
      selectableTaskEntries[0];
    if (!selectedEntry) return 'Aufgabe waehlen...';
    return dom.state.isCompact ? selectedEntry.fullTaskNumber : `Aufgabe ${selectedEntry.fullTaskNumber}`;
  };

  const selectTask = (entry: SelectableTaskEntry | null | undefined) => {
    if (!entry) return;
    setCurrentMainTaskIdx(entry.mainTaskIdx);
    setCurrentSubTaskIdx(entry.subTaskIdx);
  };

  const goToPrevTask = () => {
    if (!selectableTaskEntries.length) return;
    if (currentSelectableTaskIndex <= 0) return;
    selectTask(selectableTaskEntries[currentSelectableTaskIndex - 1]);
  };

  const goToNextTask = () => {
    if (!selectableTaskEntries.length) return;
    if (currentSelectableTaskIndex < 0 || currentSelectableTaskIndex >= selectableTaskEntries.length - 1) return;
    selectTask(selectableTaskEntries[currentSelectableTaskIndex + 1]);
  };

  const getConfidenceLevelForSubmission = (submission: Submission | undefined): 'none' | 'medium' | 'low' => {
    if (!submission) return 'none';
    const submissionMainTask = submission.taskSubmissions?.[currentMainTaskIdx];
    const gradingResultRaw =
      currentSubTaskIdx !== null
        ? getSubTaskByFlatIndex(submissionMainTask, currentSubTaskIdx)?.gradingResult
        : submissionMainTask?.gradingResult;
    const gradingResult = ((gradingResultRaw as any)?.doc || gradingResultRaw) as GradingResult | undefined;
    const boxes =
      currentSubTaskIdx !== null
        ? getSubTaskByFlatIndex(submissionMainTask, currentSubTaskIdx)?.boundingBoxes
        : submissionMainTask?.boundingBoxes;
    return getConfidenceLevelSignal(gradingResult, boxes);
  };

  const hasLowConfidenceBoxForSubmission = (submission: Submission | undefined): boolean =>
    getConfidenceLevelForSubmission(submission) === 'low';

  const hasBoxesOnPage = (pageIdx: number) => localTaskBoxes.some(box => box.pageNumber === pageIdx);

  const saveUpdate = async (boxId: string | null, data: any) => {
    if (currentSubmission?._id && currentMainTask?._id) {
      await onSaveUpdate(currentSubmission._id, currentMainTask._id, currentSubTask?._id || null, boxId, data);
    }
  };

  // Zusammenführung von State und aus DOM ausgelagerten Elementen
  return {
    refs: dom.refs,
    state: {
      ...dom.state,
      examId: exam?._id,
      mode,
      currentSubmissionIdx,
      currentMainTaskIdx,
      currentSubTaskIdx,
      currentPage,
      selectedBoxId,
      localTaskBoxes,
      isEditingText,
      editedText,
      isEditingGrading,
      editedGradingText,
      editedScore,
      localIsApproved,
      localLanguageUseIsApproved,
      isGradingSchemeCollapsed,
      boxMode,
      activeLanguageUseSourceKey,
    },
    computed: {
      role,
      submissions,
      currentSubmission,
      pages,
      currentMainTask,
      currentSubTask,
      currentMainExamTask,
      mode,
      isGrammarMode,
      currentGrammarEvaluation,
      showExcerptOnlyForMainTask,
      selectedBox,
      confidenceNum,
      selectedBoxIsApproved,
      currentMaxScore,
      displayedScore,
      currentGradingResult,
      hasLowConfidenceBoxForSubmission,
      getConfidenceLevelForSubmission: getSubmissionConfidenceLevel,
      getTaskConfidenceLevel,
      hasBoxesOnPage,
      selectableTaskEntries,
      currentSelectableTaskIndex,
      gradingSchemeCriteria,
      gradingSchemeMaxPoints,
      languageUseSchemeRules,
      languageUseBoxSources,
      activeLanguageUseSource,
      currentGroupGradingScheme,
      currentLanguageUseRuleResult,
      currentLanguageUseMaxScore,
      currentLanguageUseCriteria,
      currentLanguageUseExplanation,
      currentLanguageUseIsApproved,
      currentLanguageUseConfidenceNum,
      languageUseDisplayedScore,
      languageUseConfidenceDotLevel,
      contentConfidenceDotLevel,
      aiGradingConfidenceLevel,
      currentSaveTarget,
      combinedUncertainties,
      isGradingSchemeContentRefining,
      isGradingSchemeLanguageUseRefining,
      isRegradingContent,
      isRegradingLanguageUse,
      isRegradingContentQueued,
      isRegradingLanguageUseQueued,
      // Server-derived (BoundingBox.ocrRerunning), not client-tracked — see its doc comment. Correct
      // from the moment this canvas mounts, regardless of whether it witnessed the request start.
      isSelectedBoxSaving: selectedBox?.ocrRerunning === true,
    },
    actions: {
      ...dom.actions,
      setMode,
      setCurrentSubmissionIdx,
      setCurrentMainTaskIdx,
      setCurrentSubTaskIdx,
      setCurrentPage,
      setSelectedBoxId,
      setLocalTaskBoxes,
      setIsEditingText,
      setEditedText,
      setIsEditingGrading,
      setEditedGradingText,
      setEditedScore,
      setLocalIsApproved,
      setLocalLanguageUseIsApproved,
      handleNextUncertainty,
      selectTask,
      goToPrevTask,
      goToNextTask,
      getTaskName,
      saveUpdate,
      setIsGradingSchemeCollapsed,
      setBoxMode,
      setActiveLanguageUseSourceKey,
      openSummary: () => onOpenCanvas?.('summary'),
    },
  };
}

export type GradingStateStore = ReturnType<typeof useGradingState>;
